const labelMap = {
  photos: "Photos",
  reel: "Reel < 60sec",
  video: "Video < 1 min",
};

export const formatRequirements = (requirements) => {
  if (!requirements) return "-";
  if (typeof requirements === "string") return requirements || "-";
  if (!Array.isArray(requirements) || requirements.length === 0) return "-";
  return requirements.map((r) => labelMap[r] || r).join(", ");
};

export const formatDisplayDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("en-IN");
};

/** Requirement label for customize + hourly float modals and detail popups */
export const resolveBookingRequirementText = (booking) => {
  if (!booking) return "";

  const quote = booking.quoteId;
  const fromRole = String(
    booking.role_accordian || quote?.role_accordian || ""
  ).trim();
  if (fromRole) return fromRole;

  const rawReqs = booking.requirements;
  if (Array.isArray(rawReqs) && rawReqs.length > 0) {
    const joined = rawReqs.filter(Boolean).map(String).join(", ").trim();
    if (joined) return joined;
  }
  if (rawReqs && typeof rawReqs === "string" && rawReqs.trim()) {
    return rawReqs.trim();
  }

  if (booking.notes && String(booking.notes).trim()) {
    return String(booking.notes).trim();
  }

  if (quote?.requirements?.length > 0) {
    return quote.requirements.filter(Boolean).join(", ");
  }

  if (quote?.photographyRequirements) {
    return String(quote.photographyRequirements).trim();
  }

  if (booking.photographyRequirements) {
    return String(booking.photographyRequirements).trim();
  }

  if (typeof booking.requirement === "string" && booking.requirement.trim()) {
    return booking.requirement.trim();
  }

  return "";
};

export const formatStatusLabel = (value) => {
  if (!value) return "-";
  return value
    .toString()
    .replace(/_/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const row = (label, value) => ({ label, value });

export const buildAdminQuoteDetailRows = (item) => {
  if (!item) return [];

  const editingLabel =
    item.editingPreferences === true
      ? "Yes, professionally edited"
      : item.editingPreferences === false
        ? "No, raw files only"
        : "-";

  return [
    row("Quote Name", item.quoteName || item.clientName || item.name),
    row("Event Type", item.eventType),
    row(
      "Event Date",
      item.eventDate || formatDisplayDate(item.startDate)
    ),
    row("End Date", formatDisplayDate(item.endDate)),
    row("Duration", item.duration || (item.eventDuration ? `${item.eventDuration} hrs` : "-")),
    row("Location", item.location),
    row("Flat / House No.", item.flatOrHouseNo),
    row("Street", item.streetName),
    row("City", item.city),
    row("State", item.state),
    row("Postal Code", item.postalCode),
    row("Previous Budget", item.previousBudget || item.budget),
    row("Current Budget", item.currentBudget || item.budget),
    row(
      "Requirement",
      item.requirements ||
        formatRequirements(item.requirementsList)
    ),
    row("Editing Preferences", editingLabel),
    row("Additional Details", item.additionalDetails),
    row("Phone", item.phone || item.clientId?.mobileNumber),
    row("Email", item.email || item.clientId?.email),
    row(
      "Service Category",
      item.serviceCategory
        ? formatStatusLabel(item.serviceCategory)
        : "-"
    ),
    row(
      "Status",
      item.status || item.quoteStatus
        ? formatStatusLabel(item.status || item.quoteStatus)
        : "-"
    ),
  ];
};

export const buildQueryDetailRows = (item) => buildAdminQuoteDetailRows(item);

export const buildShootBookingDetailRows = (item) => {
  if (!item) return [];

  const requirementText =
    resolveBookingRequirementText(item) || item.requirements || item.note;

  return [
    row("Booking ID", item.veroaBookingId || item.bookingId),
    row("Client Name", item.client_name),
    row("Requirement", requirementText),
    row("Event Type", item.eventType),
    row("Event Start Date", formatDisplayDate(item.startDate)),
    row("Event End Date", formatDisplayDate(item.endDate)),
    row("Event Location", item.location || item.address),
    row("Booking Amount", item.bookingAmount ? `₹ ${item.bookingAmount}` : "-"),
    row("Payment Mode", formatStatusLabel(item.paymentMode)),
    row("Payment Status", formatStatusLabel(item.paymentStatus)),
    row(
      "Booking Status",
      item.displayStatus || formatStatusLabel(item.status || item.bookingStatus)
    ),
    row("Assigned Photographer", item.assigned_photographer),
    row("Team / Studio", item.team_studio),
    row("Gallery Status", formatStatusLabel(item.galleryStatus)),
    row("Requirements / Notes", item.requirements || item.note),
    row("Cancel Reason", item.cancelReason),
  ];
};

const formatHourlyPackageBlock = (pkg, index) => {
  const services = (pkg.services || [])
    .map((s) => `${s.qty || 1} × ${s.name} — ₹${Math.round(s.price || 0).toLocaleString("en-IN")}`)
    .join("\n");
  const assigned = [
    pkg.assignedPhotographerNames?.length
      ? `Photographers: ${pkg.assignedPhotographerNames.join(", ")}`
      : null,
    pkg.assignedVideographerNames?.length
      ? `Videographers: ${pkg.assignedVideographerNames.join(", ")}`
      : null,
    pkg.assignedEditorNames?.length
      ? `Editors: ${pkg.assignedEditorNames.join(", ")}`
      : null,
    pkg.assignedLightingNames?.length
      ? `Lighting: ${pkg.assignedLightingNames.join(", ")}`
      : null,
  ]
    .filter(Boolean)
    .join("\n");

  return [
    `Package ${index + 1}: ${pkg.hours || "-"} hrs · ${pkg.category || "Standard"}`,
    services || "No services listed",
    assigned || "No staff assigned",
  ].join("\n");
};

export const buildHourlyBookingDetailRows = (booking) => {
  if (!booking) return [];

  const packagesText = (booking.hourlyPackages || [])
    .map((pkg, i) => formatHourlyPackageBlock(pkg, i))
    .join("\n\n");

  const total =
    booking.finalAmount ||
    booking.totalAmount ||
    (booking.hourlyPackages || []).reduce(
      (sum, pkg) =>
        sum +
        (pkg.subTotal ||
          (pkg.services || []).reduce((s, svc) => s + (svc.price || 0), 0)),
      0
    );

  return [
    row("Booking ID", booking.bookingId),
    row("Client Name", booking.clientName),
    row("Event Type", booking.eventType || booking.shootType),
    row(
      "Date",
      booking.hourlyPackages?.[0]?.eventDate
        ? formatDisplayDate(booking.hourlyPackages[0].eventDate)
        : formatDisplayDate(booking.startDate)
    ),
    row("Event Address", booking.eventAddress || booking.address),
    row("Total Amount", total ? `₹ ${Math.round(total).toLocaleString("en-IN")}` : "-"),
    row("Payment Status", formatStatusLabel(booking.paymentStatus)),
    row(
      "Booking Status",
      booking.displayStatus ||
        formatStatusLabel(booking.bookingStatus || booking.status)
    ),
    row("Gallery Status", formatStatusLabel(booking.galleryStatus)),
    row("Requirement", resolveBookingRequirementText(booking)),
    row("Packages", packagesText || "-"),
  ];
};

export const buildEditingBookingDetailRows = (booking) => {
  if (!booking) return [];

  const packagesText = (booking.videoPackages || [])
    .map((pkg, i) => {
      const editors =
        pkg.assignedEditorNames?.length > 0
          ? pkg.assignedEditorNames.join(", ")
          : "—";
      return [
        `Plan ${i + 1}: ${pkg.type || pkg.planCategory || "Standard"}`,
        `Videos: ${pkg.videosCount ?? "-"}`,
        `Price: ₹${Math.round(pkg.price || 0).toLocaleString("en-IN")}`,
        `Assigned Editor: ${editors}`,
      ].join("\n");
    })
    .join("\n\n");

  const total = booking.finalAmount || booking.totalAmount;

  return [
    row("Booking ID", booking.bookingId),
    row("Client Name", booking.clientName),
    row("Event Type", booking.eventType || booking.shootType),
    row(
      "Delivery Date",
      formatDisplayDate(
        booking.endDate || booking.bookingDate || booking.eventDate || booking.startDate
      )
    ),
    row("Location", booking.eventAddress || booking.location || booking.address),
    row(
      "Total Amount",
      total ? `₹ ${Math.round(total).toLocaleString("en-IN")}` : "-"
    ),
    row("Payment Status", formatStatusLabel(booking.paymentStatus)),
    row(
      "Booking Status",
      formatStatusLabel(booking.bookingStatus || booking.status)
    ),
    row("Gallery Status", formatStatusLabel(booking.galleryStatus)),
    row("Video Packages", packagesText || "-"),
  ];
};
